import { Loader2 } from "lucide-react";
import type { DriveImportFailure } from "@gb/contracts";
import { cn } from "@/lib/utils";

/**
 * A picked file that did not arrive. One refused up front carries its Drive
 * id; one the worker could not fetch is reported on its import, by name alone.
 */
type Failure = Pick<DriveImportFailure, "name" | "reason"> & { fileId?: string };

/**
 * The picked files an import could not take.
 *
 * Its own component, and not inlined into the step, so the one thing worth
 * asserting — that every failed file is named — can be asserted without
 * standing up the eight hooks that screen depends on.
 *
 * Warn rather than danger: whatever else was picked has arrived and is listed
 * below this. Nothing here needs undoing, and a red block over a mostly
 * successful import overstates it.
 */
export function DriveImportFailures({ failures }: { failures: Failure[] }) {
  if (failures.length === 0) return null;

  return (
    <div
      role="alert"
      className="rounded-input border-[1.5px] border-warn/40 bg-warn-bg px-4 py-3"
    >
      <p className="text-[0.85rem] font-semibold text-warn">
        {failures.length === 1
          ? "1 file couldn’t be imported from Google Drive."
          : `${failures.length} files couldn’t be imported from Google Drive.`}
      </p>
      {/* Named one by one. "Some files failed" leaves the user to diff the
          Picker against the listing themselves, which is the work this exists
          to save them. */}
      <ul className="mt-2 flex flex-col gap-1">
        {failures.map((failure, index) => (
          // A failure reported by the import has no Drive id, and two picked
          // files can share a name, so those are keyed by position.
          <li key={failure.fileId ?? `lost-${index}`} className="text-[0.82rem] text-warn-text">
            <span className="font-semibold">{failure.name}</span> — {failure.reason}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Picked files still on their way.
 *
 * The pick is accepted before anything is fetched (`architecture/0028`). A
 * local upload has a row with a progress bar until it lands; a Drive file has
 * nothing, so without this the screen looks as if the pick did nothing.
 */
export function DriveImportArriving({
  count,
  className,
}: {
  count: number;
  className?: string;
}) {
  if (count === 0) return null;

  return (
    <p role="status" className={cn("flex items-center gap-2 text-[0.82rem] text-muted", className)}>
      <Loader2 size={14} className="animate-spin" aria-hidden />
      {count === 1
        ? "Importing 1 file from Google Drive…"
        : `Importing ${count} files from Google Drive…`}
    </p>
  );
}
